import { createWebHistory, createRouter, useRouter } from "vue-router";
import DefaultLayout from "../views/layout/DefaultLayout.vue";
import Dashboard from "../views/user/Dashboard.vue";
import Login from "../views/Login.vue";
import Register from "../views/Register.vue";
import useAuthToken from "../composables/useAuthToken";
import useUserData from "/@/composables/useUserData";
import { applicationTypes } from "/@/types/ApplicationType.type";
import { cardRenewalCreateNewRoute } from "./card-renewal-create-new";
import { frontDoor } from "./front-door";
import { STICKY_HEADER_OFFSET, scrollTarget } from "./scroll-target";

const routes = [
  {
    path: "/",
    name: "home",
    component: DefaultLayout,
    meta: { requiresAuth: true },
    redirect: { name: "home-index" },
    children: [
      {
        path: "",
        name: "home-index",
        component: () => import("../views/home/HomeIndex.vue"),
        meta: { requiresOnboarding: true },
      },
      {
        path: "dashboard",
        name: "dashboard",
        component: Dashboard,
      },
      {
        path: "my-account",
        name: "my-account",
        component: () => import("../views/user/MyAccount.vue"),
        redirect: { name: "settings" },
        children: [
          {
            path: "settings",
            name: "settings",
            component: () => import("../views/user/GeneralSettings.vue"),
            meta: { requiresOnboarding: true },
          },
          {
            path: "claimant-information",
            name: "claimant-info",
            component: () => import("../views/user/ClaimaintInfo.vue"),
          },
          {
            path: "banking-details",
            name: "banking-details",
            component: () => import("../views/user/BankingDetails.vue"),
            meta: { requiresOnboarding: true },
          },
        ],
      },
      {
        path: "benefits",
        name: "benefits-index",
        component: () => import("../views/benefits/BenefitsIndex.vue"),
        meta: { requiresOnboarding: true },
      },
      {
        path: "claims",
        name: "claims",
        component: () => import("../views/claims/ClaimsLayout.vue"),
        redirect: { name: "claims-index" },
        meta: { requiresOnboarding: true },
        children: [
          {
            path: "",
            name: "claims-index",
            component: () => import("../views/claims/home/ClaimsHome.vue"),
          },
          {
            path: "my-claims",
            name: "my-claims",
            component: () => import("../views/claims/index.vue"),
          },
          {
            path: ":id",
            name: "claims-show",
            component: () => import("../views/claims/show.vue"),
          },
          // Funeral routes
          {
            path: applicationTypes.Funeral.indexPage.path,
            name: applicationTypes.Funeral.indexPage.pathName,
            component: () => import("../views/claims/funeral/index.vue"),
          },
          {
            path: applicationTypes.Funeral.createPage.path,
            name: applicationTypes.Funeral.createPage.pathName,
            component: () => import("../views/claims/funeral/create.vue"),
          },

          // Injury routes

          {
            path: applicationTypes.Injury.indexPage.path,
            name: applicationTypes.Injury.indexPage.pathName,
            component: () => import("../views/claims/injury/index.vue"),
          },
          {
            path: applicationTypes.Injury.createPage.path,
            name: applicationTypes.Injury.createPage.pathName,
            component: () => import("../views/claims/injury/create.vue"),
          },

          // Retirement routes
          {
            path: applicationTypes.Retirement.indexPage.path,
            name: applicationTypes.Retirement.indexPage.pathName,
            component: () => import("../views/claims/retirement/index.vue"),
          },
          {
            path: applicationTypes.Retirement.createPage.path,
            name: applicationTypes.Retirement.createPage.pathName,
            component: () => import("../views/claims/retirement/create.vue"),
          },

          // Sickness routes
          {
            path: applicationTypes.Sickness.indexPage.path,
            name: applicationTypes.Sickness.indexPage.pathName,
            component: () => import("../views/claims/sickness/index.vue"),
          },
          {
            path: applicationTypes.Sickness.createPage.path,
            name: applicationTypes.Sickness.createPage.pathName,
            component: () => import("../views/claims/sickness/create.vue"),
          },

          // Sickness Ext routes
          {
            path: applicationTypes.Sickness_Extension.indexPage.path,
            name: applicationTypes.Sickness_Extension.indexPage.pathName,
            component: () => import("../views/claims/sicknessext/index.vue"),
          },
          {
            path: applicationTypes.Sickness_Extension.createPage.path,
            name: applicationTypes.Sickness_Extension.createPage.pathName,
            component: () => import("../views/claims/sicknessext/create.vue"),
          },

          // Maternity routes
          {
            path: applicationTypes.Maternity.indexPage.path,
            name: applicationTypes.Maternity.indexPage.pathName,
            component: () => import("../views/claims/maternity/index.vue"),
          },
          {
            path: applicationTypes.Maternity.createPage.path,
            name: applicationTypes.Maternity.createPage.pathName,
            component: () => import("../views/claims/maternity/create.vue"),
          },
          // Maternity Ext routes
          {
            path: applicationTypes.Maternity_Extension.indexPage.path,
            name: applicationTypes.Maternity_Extension.indexPage.pathName,
            component: () => import("../views/claims/maternityext/index.vue"),
          },
          {
            path: applicationTypes.Maternity_Extension.createPage.path,
            name: applicationTypes.Maternity_Extension.createPage.pathName,
            component: () => import("../views/claims/maternityext/create.vue"),
          },
          // Unemployment routes
          {
            path: applicationTypes.Unemployment.indexPage.path,
            name: applicationTypes.Unemployment.indexPage.pathName,
            component: () => import("../views/claims/unemployment/index.vue"),
          },
          {
            path: applicationTypes.Unemployment.createPage.path,
            name: applicationTypes.Unemployment.createPage.pathName,
            component: () => import("../views/claims/unemployment/create.vue"),
          },
        ],
      },
      {
        path: "registration",
        name: "registration",
        component: () => import("../views/applications/ApplicationsLayout.vue"),
        redirect: { name: "card-renewal-index" },
        meta: { requiresOnboarding: true },
        children: [
          {
            path: "card-renewal",
            name: "card-renewal",
            component: () =>
              import("../views/applications/card-renewal/CardRenewal.vue"),
            children: [
              {
                path: "",
                name: "card-renewal-index",
                component: () =>
                  import("../views/applications/card-renewal/Home.vue"),
              },
              {
                path: "applications",
                name: "card-renewal-applications",
                component: () =>
                  import("../views/applications/card-renewal/Index.vue"),
              },
              {
                path: "create",
                name: "card-renewal-create",
                component: () =>
                  import("../views/applications/card-renewal/Create.vue"),
              },
              // Redirects to the guarded card-renewal-create route; see the
              // module for why the stub view is no longer rendered.
              cardRenewalCreateNewRoute,
              {
                path: ":id",
                name: "card-renewal-show",
                component: () =>
                  import("../views/applications/card-renewal/Show.vue"),
              },
            ],
          },
        ],
      },
    ],
  },
  {
    path: "/welcome",
    name: "welcome",
    component: () => import("../views/Welcome.vue"),
    meta: { guest: true },
  },
  {
    path: "/login",
    name: "login",
    component: Login,
    meta: { guest: true },
  },
  {
    path: "/maintenance",
    name: "maintenance",
    component: () => import("../views/home/MaintenanceMode.vue"),
    meta: { maintenance: true}
  },
  {
    path: "/account-activation",
    name: "activate-account",
    redirect: (to: any) => ({
      name: "login",
      query: { token: to.query.token },
    }),
  },
  {
    path: "/register",
    name: "register",
    component: Register,
    meta: { guest: true },
  },
  {
    path: "/reset-password",
    name: "reset-password",
    component: () => import("../views/user/ResetPassword.vue"),
  },
  {
    path: "/reset-email",
    name: "reset-email",
    component: () => import("../views/user/ResetEmail.vue"),
  },
  {
    path: "/404/not-found",
    name: "404",
    component: () => import("../views/404.vue"),
  },
  {
    path: "/:pathMatch(.*)*",
    redirect: { name: "404" },
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    // Welcome.vue keeps this in step with its sticky header's live height.
    // 0 is a real value (the header is not sticky on phones), so only a
    // missing or unreadable variable falls back.
    const published = parseInt(
      getComputedStyle(document.documentElement).getPropertyValue(
        "--welcome-header-h"
      )
    );
    const headerOffset = Number.isNaN(published)
      ? STICKY_HEADER_OFFSET
      : published;
    return scrollTarget(to, savedPosition, headerOffset);
  },
});

router.beforeEach(async (to, from) => {
  // instead of having to check every route record with
  // to.matched.some(record => record.meta.requiresAuth
  const { isLoggedIn } = useAuthToken();
  const { isOnboarded } = useUserData();

  const frontDoorTarget = frontDoor(to.path, isLoggedIn());
  if (frontDoorTarget) return frontDoorTarget;

  if (to.meta.requiresAuth && !isLoggedIn()) {
    // this route requires auth, check if logged in
    // if not, redirect to login page.
    return {
      name: "login",
      // save the location we were at to come back later
      query: { redirect: to.fullPath },
    };
  }

  if (to.meta.requiresOnboarding) {
    // check if user has been onboarded
    // if not, redirect to my account page.
    //
    // Carry the destination. Citizen emails now link straight to one
    // application, and a citizen who has not yet given us their claimant
    // information was being dropped on this page with no way back to the thing
    // the email asked them to do. ClaimaintInfo resumes it once the three
    // sections are complete.
    if (!isOnboarded()) {
      return {
        name: "claimant-info",
        query: { redirect: to.fullPath },
      };
    }
  }

  if (to.meta.guest && isLoggedIn()) {
    // this route does not require auth, check if logged in
    // if not, redirect to login page.
    return {
      path: from.fullPath,
    };
  }
});

export default router;
